<template>
  <div>
    <v-container class="pt-3 pb-6 px-6">

      <!-- Page Header -->
      <page-header
        :title="title + ' Revisión de Intervención - ' + $route.params.id"
        icon="mdi-magnify"
        divider="-"
        :breadcrumbs="breadcrumbs.items"
        refresh
        @refresh="init()"
      ></page-header>

      <v-row align="center" justify="space-around">

        <!-- Loading Bar -->
        <v-progress-linear
          indeterminate
          v-if="isLoadingData"
        ></v-progress-linear>

        <!-- Content -->
        <v-col cols="12" v-if="authUser">
          <v-window v-model="step">

            <v-window-item :value="1">
              <v-switch
                v-if="showEdit && !isReadonly"
                label="Editar"
                v-model="isEditing"
                class="mt-0 ml-4"
                color="primary"
              ></v-switch>

              <v-row align="start" justify="space-around" v-show="step === 1">

                <!-- Intervention Information -->
                <v-col cols="12" class="pt-0">
                  <v-card>
                    <v-card-text>
                      <v-container class="py-0">
                        <v-row align="start">
                          <!-- Work Order -->
                          <v-col cols="2" class="pt-0">
                            <v-subheader class="pl-0 font-weight-bold">Código OT</v-subheader>
                            <span class="body-1">{{ work_order.code }}</span>
                          </v-col>
                          <!-- Type -->
                          <v-col cols="3" class="pt-0">
                            <v-subheader class="pl-0 font-weight-bold">Tipo de trabajo</v-subheader>
                            <span class="body-1">{{ work_order.work_type }}</span>
                          </v-col>
                          <!-- -->
                          <v-col cols="5" class="pt-0">
                            <v-row>
                              <!-- Farm -->
                              <v-col cols="4" class="py-0">
                                <v-subheader class="pl-0 font-weight-bold">Parque</v-subheader>
                                <span class="body-1">{{ item.wind_farm && item.wind_farm.name }}</span>
                              </v-col>
                              <!-- Technicians -->
                              <v-col cols="8" class="py-0">
                                <v-subheader class="pl-0 font-weight-bold">Operarios</v-subheader>
                                <span class="body-1">{{ techniciansName(item.users) }}</span>
                              </v-col>
                            </v-row>
                          </v-col>
                          <!-- State -->
                          <v-col cols="2" class="pt-0">
                            <v-select
                              v-model="currentState"
                              :items="stateItems"
                              class="pt-4"
                              label="Estado"
                              outlined
                              :disabled="isReadonly"
                              @change="changeState($event)"
                            ></v-select>
                          </v-col>
                        </v-row>
                      </v-container>
                    </v-card-text>
                  </v-card>
                </v-col>

                <!-- Damage List -->
                <v-col cols="12">
                  <v-card>
                    <v-card-text class="pt-0">
                      <v-data-table
                        :headers="damageHeaders"
                        :items="bladeDamages"
                        :items-per-page="5"
                        :key="component"
                      >
                        <!-- Top -->
                        <template v-slot:top>
                          <v-toolbar flat color="white">
                            <span class="title">Daños</span>
                            <v-text-field
                              v-model="damageFilter"
                              placeholder="Buscar..."
                              prepend-icon="mdi-magnify"
                              class="mx-5"
                              clearable
                              single-line
                              hide-details
                            ></v-text-field>
                            <v-btn v-if="isEditing" small color="primary" @click="clickDamage()">Añadir</v-btn>
                          </v-toolbar>
                        </template>
                        <!-- Damage -->
                        <template v-slot:item.damage="{ item }">
                          {{ bladeDamages.indexOf(item) + 1 }}
                        </template>
                        <!-- Date -->
                        <template v-slot:item.date="{ item }">
                          {{ formatDate(item.date) }}
                        </template>
                        <!-- Ref -->
                        <template v-slot:item.damage_similars="{ item }">
                          <span :class="isAsociado(item)">{{ bladeDamageSimilarsCount(item) }}</span>
                        </template>
                        <!-- Actions -->
                        <template v-slot:item.action="{ item }">
                          <v-icon class="mr-2" :disabled="!bladeDamageSimilarsCount(item) && !isEditing" @click="refDamage(item)">mdi-link-variant</v-icon>
                          <span v-if="isEditing">
                            <v-icon class="mr-2" @click="clickDamage(item)">mdi-pencil</v-icon>
                            <v-icon @click="deleteDamage(item)">mdi-delete</v-icon>
                          </span>
                          <span v-else>
                            <v-icon @click="clickDamage(item)">mdi-eye</v-icon>
                          </span>
                        </template>
                      </v-data-table>
                    </v-card-text>
                  </v-card>
                </v-col>

                <!-- Days List -->
                <v-col cols="12">
                  <v-card>
                    <v-card-text class="pt-0">
                      <v-data-table
                        :headers="daysHeaders"
                        :items="days"
                        :items-per-page="5"
                        :key="component"
                      >
                        <!-- Top -->
                        <template v-slot:top>
                          <v-toolbar flat color="white">
                            <span class="title">Días de intervención</span>
                            <v-menu
                              v-model="menu"
                              :close-on-content-click="false"
                              :nudge-right="40"
                              transition="scale-transition"
                              offset-y
                              min-width="290px"
                            >
                              <template v-slot:activator="{ on }">
                                <v-text-field
                                  v-model="dayFilter"
                                  placeholder="Fecha"
                                  class="shrink mx-5"
                                  hide-details
                                  single-line
                                  readonly
                                  v-on="on"
                                ></v-text-field>
                              </template>
                              <v-date-picker v-model="dayFilter" @input="menu = false"></v-date-picker>
                            </v-menu>
                            <v-spacer/>
                            <v-btn v-if="isEditing" small color="primary" @click="clickDay()">Añadir</v-btn>
                          </v-toolbar>
                        </template>
                        <!-- Day -->
                        <template v-slot:item.day="{ item }">
                          {{ days.indexOf(item) + 1 }}
                        </template>
                        <!-- Date -->
                        <template v-slot:item.date="{ item }">
                          {{ formatDate(item.date) }}
                        </template>
                        <!-- AG -->
                        <!-- <template v-slot:item.wind_turbine="{ item }">
                          {{ dayWorkedWindTurbine(item) }}
                        </template> -->
                        <!-- Actions -->
                        <template v-slot:item.action="{ item }">
                          <span v-if="isEditing">
                            <v-icon class="mr-2" @click="clickDay(item)">mdi-pencil</v-icon>
                            <v-icon @click="deleteDay(item)">mdi-delete</v-icon>
                          </span>
                          <span v-else>
                            <v-icon @click="clickDay(item)">mdi-eye</v-icon>
                          </span>
                        </template>
                      </v-data-table>
                    </v-card-text>
                  </v-card>
                </v-col>

                <!-- Panoramic Images -->
                <v-col cols="12" v-if="work_order.work_type_id === inspeccion">
                  <panoramic-images :isEditing="isEditing"/>
                </v-col>

                <!-- Sensors Box Chart -->
                <v-col cols="12" v-if="work_order.work_type === reparacion">
                  <v-expansion-panels>
                    <v-expansion-panel>
                      <v-expansion-panel-header>Gráficos</v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <sensors-box-chart flat />
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-col>

              </v-row>
            </v-window-item>

            <!-- Add/Edit Day -->
            <v-window-item :value="2">
              <edit-day
                v-if="step === 2"
                :isEditing="isEditing"
                :day="selectedItem"
                :work_type="work_order.work_type_id"
                @init="getDays()"
                @clickBack="clickBack()"
              ></edit-day>
            </v-window-item>

            <!-- Add/Edit Damage -->
            <v-window-item :value="3">
              <edit-damage
                v-if="step === 3"
                :isEditing="isEditing"
                :damage="selectedItem"
                :work_type="work_order.work_type_id"
                @init="getDamages()"
                @clickBack="clickBack()"
              ></edit-damage>
            </v-window-item>

          </v-window>
        </v-col>

      </v-row>
    </v-container>

    <!-- Ref Damage -->
    <ref-damage
      v-model="refDamageDialog"
      :isEditing="isEditing"
      :item="selectedItem"
      @close="refDamageDialog = false, component++"
      @init="initDamages()"
    />

    <!-- Confirmation -->
    <confirmation
      v-if="authUser"
      v-model="confirmDialog"
      :title="confirmTitle"
      :text="confirmText"
      :item="confirmItem"
      :textButton="textButton"
      :colorButton="colorButton"
      @close="closeConfirm(true)"
      @confirm="confirm(confirmAction)"
    />

  </div>
</template>

<style>
.border-right {
  border-right: thin solid rgba(0, 0, 0, 0.12)
}
</style>

<script>
import { validationMixin } from 'vuelidate';
import { required } from 'vuelidate/lib/validators';
import Util from "@/util/index";

import PageHeader from "@/components/PageHeader";
import Confirmation from "@/components/Confirmation";
import EditDay from "@/components/InterventionReview/Day/Day";
import EditDamage from "@/components/InterventionReview/Damage/Damage";
import RefDamage from "@/components/InterventionReview/RefDamage";
import PanoramicImages from "@/components/InterventionReview/PanoramicImages";
import SensorsBoxChart from '@/components/SensorsBoxChart'
import STATES from '@/models/States'
import ROLES from '@/models/Roles'


export default {
  components: {
    PageHeader,
    Confirmation,
    EditDay,
    EditDamage,
    RefDamage,
    PanoramicImages,
    SensorsBoxChart
  },
  mixins: [validationMixin],
  validations: {
    work_order: {
      code: { required },
      work_type: { required },
      state: { required },
      wind_farms: { required },
      contract_award_id: { required },
      summary: { required }
    },
    client: { required }
  },
  data: () => ({
    currentState: '',
    component: 0,
    isEditing: false,
    roles: ROLES,
    STATE_ID: STATES.INTERVENTION_REVIEW_STATE_ID,
    STATE_TEXT: STATES.INTERVENTION_REVIEW_STATE_TEXT,
    stateItems: STATES.INTERVENTION_REVIEW_STATES,
    inspeccion: STATES.WORK_ORDER_TYPE_INSPECCION,
    reparacion: STATES.WORK_ORDER_TYPE_REPARACION,
    item: {},
    selectedItem: {},
    work_order: {},
    // Confirm Dialog
    confirmDialog: false,
    confirmTitle: '',
    confirmText: '',
    confirmItem: '',
    confirmAction: '',
    textButton: '',
    colorButton: '',
    //
    menu: false,
    dayFilter: '',
    showDay: 0,
    day: 0,
    daysHeaders: [
      { text: "Día", value: "day", align: "center" },
      { text: "Fecha", value: "date", align: "center" },
      // { text: "AG", value: "wind_turbine", align: "center" },
      { text: "Acciones", value: "action", sortable: false, align: "center" }
    ],
    // Damage
    refDamageDialog: false,
    damageFilter: '',
    image_upload: null,
    damageHeaders: [
      { text: "Daño", value: "damage", align: "center" },
      { text: "AG", value: "wind_turbine", align: "center" },
      { text: "Posición", value: "location", align: "center" },
      { text: "Código del daño", value: "blade_damage_type.name", align: "center" },
      { text: "Grado", value: "severity", align: "center" },
      { text: "Daños asociados", value: "damage_similars", align: "center" },
      { text: "Acciones", value: "action", sortable: false, align: "center" }
    ],
    //
    newOrderId: null,
    authUser: false,
    step: 1,
    isLoadingFiles: false,
    isLoadingData: false,
    client: null,
    file: null,
    dialog: false,
    uploadingFileDialog: false,
    fileToDelete: {},
    oldCode: '',
    alert: true,
    typeItems: STATES.WORK_ORDER_TYPE,
    breadcrumbs: {
      items: [
        { text: "Configuración", disabled: true },
        { text: "Revisión de Intervenciones", disabled: true }
      ]
    },
  }),
  watch: {
    step(val) {
      if (val === 1) {
        this.getDamages()
        this.getDays()
      }
    }
  },
  computed: {
    isReadonly() {
      return this.$store.getters['IS_READONLY']
    },
    loading: {
      get () {
        return this.$store.state.loading
      },
      set (val) {
        this.$store.commit('loading', val)
      }
    },
    title() {
      return this.isEditing ? 'Editar' : 'Visualizar'
    },
    showEdit() {
      return this.item.state_id === this.STATE_TEXT.EN_REVISION
    },
    uploadingFile () {
      return this.$store.getters['orders/uploadingFile']
    },
    intervention () {
      return this.$store.state.interventions.item
    },
    days () {
      return this.$store.state.interventions.days.list
    },
    bladeDamageSimilars () {
      return this.$store.state.interventions.bladeDamageSimilars.list
    },
    bladeDamages () {
      return this.$store.state.interventions.bladeDamages.list
    },
    bladeDamageTypes () {
      return this.$store.state.interventions.options.bladeDamageTypes
    },
    farms () {
      if (this.client) {
        let farms = []
        this.clients.filter(element => {
          if (element.id === this.client) farms = element.wind_farms
        })
        return farms
      } else {
        return null
      }
    },
    technicians () {
      return this.$store.state.users.all.filter(element => {
        return element.roles.filter(e => e.name === 'Operario').length > 0 && element.isActive === true
      })
    },
    codeErrors () {
      const errors = []
      if (!this.$v.work_order.code.$dirty) return errors
      !this.$v.work_order.code.required && errors.push('Obligatorio.')
      return errors
    },
    typeErrors () {
      const errors = []
      if (!this.$v.work_order.work_type.$dirty) return errors
      !this.$v.work_order.work_type.required && errors.push('Obligatorio.')
      return errors
    },
    stateErrors () {
      const errors = []
      if (!this.$v.work_order.state.$dirty) return errors
      !this.$v.work_order.state.required && errors.push('Obligatorio.')
      return errors
    },
  },
  methods: {
    formatDate(date) {
      return Util.formatDate(date)
    },
    clickBack () {
      this.step = 1
      this.selectedItem = {}
    },
    errorAlert (error, altText) {
      if (error.response.status === 403 ) {
        this.$toast("Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.", {
          color: "error",
          x: "center",
          y: "top",
          icon: "mdi-information",
          timeout: 0,
          showClose: true,
          closeText: "Cerrar"
        });
        this.$router.push({ name: 'home'})
      } else {
        let errorToastMessage = `Ha ocurrido un error ${altText}`
        Util.showErrorToast(this, error, errorToastMessage);
      }
    },
    isAsociado(item) {
      const damages = this.bladeDamageSimilars.filter(element => element.similar_blade_damage_id === item.id)
      return damages.length ? 'error--text font-weight-bold' : ''
    },
    bladeDamageSimilarsCount(item) {
      const filtered = this.bladeDamageSimilars.filter(element => {
        return element.blade_damage_id === item.id
          || element.similar_blade_damage_id === item.id
      })
      return filtered.length
    },
    farmsName (farms) {
      if (farms && farms.length > 0) return farms.map(el => el.name).join(', ')
    },
    techniciansName (technicians) {
      if (technicians && technicians.length > 0)  {
        return technicians.map(el => {
          let name = el.first_name
          if (el.last_name1) name += ' ' + el.last_name1
          if (el.last_name2) name += ' ' + el.last_name2
          return name
        }).join(', ')
      }
    },
    init (init) {
      this.isLoadingData = true;
      const body = {
        id: this.$route.params.id,
        work_order_id: this.$route.params.work_order,
        with: 'workOrder,users,windFarm'
      }
      this.$store.dispatch('interventions/get', body)
        .then(async response => {
          const data = response.data.data
          this.item = data
          this.currentState = data.state_id
          if (data.work_order) this.work_order = data.work_order
          if (init) {
            await this.$store.dispatch('interventions/options/getBladeDamageTypes')
            // await this.$store.dispatch('interventions/bladeDamageIsInBladeZones/list')
            await this.$store.dispatch('interventions/bladeZones/list')
            await this.getLists()
            await this.getDays()
            await this.initDamages()
          }
          this.authUser = true
        })
        .catch(error => {
          if (error && error.response && error.response.status === 403 ) {
            this.$toast("Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.", {
              color: "error",
              x: "center",
              y: "top",
              icon: "mdi-information",
              timeout: 0,
              showClose: true,
              closeText: "Cerrar"
            });
            this.$router.push({ name: 'home'})
          } else {
            var errorToastMessage = "cargando las intervenciones."
            this.errorAlert(error, errorToastMessage)
          }
        })
        .finally(() => {
          this.isLoadingData = false
        })
    },
    // Confirm Dialog
    confirm (action) {
      console.log("confirm -> action", action)
      if (action === 'changeState') this.changeState()
      if (action === 'deleteDay') this.deleteDay()
      if (action === 'deleteDamage') this.deleteDamage()
    },
    closeConfirm (init) {
      this.confirmDialog = false
      this.confirmTitle = ''
      this.confirmText = ''
      this.confirmItem = ''
      this.textButton = ''
      this.colorButton = ''
      this.selectedItem = {}
      if (init) this.init()
    },
    // Change State
    changeState (state) {
      if (state) {
        this.confirmAction = 'changeState'
        this.confirmTitle = 'Modificar estado'
        this.confirmText = 'Desea modificar el estado a'
        this.confirmItem = this.STATE_ID[state]
        this.textButton = 'Modificar'
        this.colorButton = 'primary'
        this.confirmDialog = true
      } else {
        const body = {
          id: this.$route.params.id,
          state_id: this.currentState
        }
        this.loading = true
        this.closeConfirm()
        this.$store.dispatch('interventions/changeState', body)
          .then(() => {
            this.isEditing = false
            this.$toast("Estado modificado correctamente.", {
              color: "success",
              x: "center",
              y: "top",
              icon: "mdi-information"
            });
          })
          .catch(error => {
            if (error.response.status === 403 ) {
              this.$toast("Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.", {
                color: "error",
                x: "center",
                y: "top",
                icon: "mdi-information",
                timeout: 0,
                showClose: true,
                closeText: "Cerrar"
              });
              this.$router.push({ name: 'home'})
            } else {
              var errorToastMessage = "modificando el estado de la intervención."
              this.errorAlert(error, errorToastMessage)
            }
          })
          .finally(() => {
            this.loading = false
            this.init()
          })
      }
    },
    // Days
    async getDays () {
      this.isLoadingData = true
      const body = {
        work_intervention_id: this.$route.params.id,
        work_order_id: this.$route.params.work_order
      }
      await this.$store.dispatch('interventions/days/list', body)
        .then(() =>{
          this.component++
        })
        .catch(error => {
          if (error.response.status === 403 ) {
            this.$toast("Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.", {
              color: "error",
              x: "center",
              y: "top",
              icon: "mdi-information",
              timeout: 0,
              showClose: true,
              closeText: "Cerrar"
            });
            this.$router.push({ name: 'home'})
          } else {
            var errorToastMessage = "cargando la lista de dias."
            this.errorAlert(error, errorToastMessage)
          }
        })
        .finally(() => {
          this.isLoadingData = false
        })
    },
    clickDay (day) {
      if (day) this.selectedItem = day
      this.step = 2
    },
    deleteDay (item) {
      if (item) {
        this.selectedItem = item
        this.confirmAction = 'deleteDay'
        this.confirmTitle = 'Eliminar día de trabajo'
        this.confirmText = 'Desea eliminar el día de trabajo'
        this.textButton = 'Eliminar'
        this.colorButton = 'deep-orange'
        this.confirmDialog = true
      } else {
        this.loading = true
        const body = {
          id: this.selectedItem.id,
          work_intervention_id: this.$route.params.id,
          work_order_id: this.$route.params.work_order
        }
        this.closeConfirm()
        this.$store.dispatch('interventions/days/delete', body)
          .then(() => {
            this.$toast("Dia eliminado correctamente.", {
              color: "success",
              x: "center",
              y: "top",
              icon: "mdi-information"
            });
            this.getDays()
          })
          .catch(error => {
            let altText = "eliminando el día."
            this.errorAlert(error, altText)
          })
          .finally(() => {
            this.loading = false
          })
      }
    },
    // Damage
    async initDamages() {
      await this.getDamageSimilars()
      await this.getDamages()
    },
    async getDamageSimilars () {
      this.isLoadingData = true
      try {
        const body1= {
          work_order_id: this.$route.params.work_order,
          with: 'bladeDamageType,windTurbineType,windTurbineBladeType'
        }
        await this.$store.dispatch('interventions/bladeDamageSimilars/damages', body1)
        const body2= {
          work_order_id: this.$route.params.work_order,
          with: 'damage,similarDamage'
        }
        await this.$store.dispatch('interventions/bladeDamageSimilars/list', body2)
        this.component++
      }
      catch (error) {
        if (error.response.status === 403 ) {
          this.$toast("Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.", {
            color: "error",
            x: "center",
            y: "top",
            icon: "mdi-information",
            timeout: 0,
            showClose: true,
            closeText: "Cerrar"
          });
          this.$router.push({ name: 'home'})
        } else {
          var errorToastMessage = "cargando la lista de daños asociados."
          this.errorAlert(error, errorToastMessage)
        }
      }
      finally {
        this.isLoadingData = false
      }
    },
    async getDamages () {
      this.isLoadingData = true
      const body = {
        work_intervention_id: this.$route.params.id,
        work_order_id: this.$route.params.work_order,
        with: 'bladeDamageType'
      }
      await this.$store.dispatch('interventions/bladeDamages/list', body)
        .then(() =>{
          this.component++
        })
        .catch(error => {
          if (error.response.status === 403 ) {
            this.$toast("Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.", {
              color: "error",
              x: "center",
              y: "top",
              icon: "mdi-information",
              timeout: 0,
              showClose: true,
              closeText: "Cerrar"
            });
            this.$router.push({ name: 'home'})
          } else {
            var errorToastMessage = "cargando la lista de daños."
            this.errorAlert(error, errorToastMessage)
          }
        })
        .finally(() => {
          this.isLoadingData = false
        })
    },
    clickDamage (item) {
      console.log("clickDamage -> item", item)
      if (item) this.selectedItem = item
      this.step = 3
    },
    refDamage (item) {
      this.selectedItem = item
      this.refDamageDialog = true
    },
    deleteDamage (item) {
      if (item) {
        this.selectedItem = item
        this.confirmAction = 'deleteDamage'
        this.confirmTitle = 'Eliminar daño'
        this.confirmText = 'Desea eliminar el daño'
        this.textButton = 'Eliminar'
        this.colorButton = 'deep-orange'
        this.confirmDialog = true
      } else {
        this.loading = true
        const body = {
          id: this.selectedItem.id,
          work_intervention_id: this.$route.params.id,
          work_order_id: this.$route.params.work_order
        }
        this.closeConfirm()
        this.$store.dispatch('interventions/bladeDamages/delete', body)
          .then(() => {
            this.$toast("Daño eliminado correctamente.", {
              color: "success",
              x: "center",
              y: "top",
              icon: "mdi-information"
            });
            this.getDamages()
          })
          .catch(error => {
            let altText = "eliminando el daño."
            this.errorAlert(error, altText)
          })
          .finally(() => {
            this.loading = false
          })
      }
    },
    // Get Lists
    async getLists () {
      this.isLoadingData = true
      await this.$store.dispatch('farms/getTurbineList')
        .then(() => {})
        .catch(error => {
          if (error.response.status === 403 ) {
            this.$toast("Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.", {
              color: "error",
              x: "center",
              y: "top",
              icon: "mdi-information",
              timeout: 0,
              showClose: true,
              closeText: "Cerrar"
            });
            this.$router.push({ name: 'home'})
          } else {
            var errorToastMessage = "cargando la lista de turbinas."
            this.errorAlert(error, errorToastMessage)
          }
        })
      await this.$store.dispatch('farms/getBladeList')
        .then(() => {})
        .catch(error => {
          if (error.response.status === 403 ) {
            this.$toast("Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.", {
              color: "error",
              x: "center",
              y: "top",
              icon: "mdi-information",
              timeout: 0,
              showClose: true,
              closeText: "Cerrar"
            });
            this.$router.push({ name: 'home'})
          } else {
            var errorToastMessage = "cargando la lista de palas."
            this.errorAlert(error, errorToastMessage)
          }
        })
        .finally(() => {
          this.isLoadingData = false
        })
    },
    //
    interventionList () {
      this.$router.push({ name: "intervention-review-list"  });
    },
  },
  created () {
    this.init(true)
  }
}
</script>
